import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { TeamCompaniesService } from '../../../services/teamcompanies/teamcompanies.service';
import { TeamCompanyUsersService } from '../../../services/teamcompanyusers/teamcompanyusers.service';

export interface AssociateCompanyRoleDialogData {
  user: any;
}

interface CompanyRoleOption {
  value: 'payrolladmin' | 'manager' | 'teamagent';
  label: string;
  description: string;
}

interface CompanyRoleAssociation {
  id: string;
  companyId: string;
  companyName: string;
  companyType?: string;
  domainName?: string;
  role: string;
}

@Component({
  selector: 'app-associate-company-role-dialog',
  templateUrl: './associate-company-role-dialog.component.html',
  styleUrls: ['./associate-company-role-dialog.component.scss'],
})
export class AssociateCompanyRoleDialogComponent implements OnInit, OnDestroy {
  public readonly roleOptions: CompanyRoleOption[] = [
    {
      value: 'payrolladmin',
      label: 'Payroll Admin',
      description: 'Recommended for Payroll Admin setup',
    },
    {
      value: 'manager',
      label: 'Team Manager',
      description: 'Payroll Admin access also needs the native access feature flag',
    },
    {
      value: 'teamagent',
      label: 'Team Agent',
      description: 'Can manage teams for the selected company using constrained team access',
    },
  ];

  public form = this.formBuilder.group({
    companyId: new UntypedFormControl('', Validators.required),
    role: new UntypedFormControl('payrolladmin', Validators.required),
  });
  public companySearchControl = new UntypedFormControl('');
  public companies: any[] = [];
  public filteredCompanies: any[] = [];
  public associations: CompanyRoleAssociation[] = [];
  public loading = false;
  public saving = false;
  public removingAssociationId = '';
  public errorMessage = '';
  public successMessage = '';

  private companySearchSubscription?: Subscription;
  private formSubscription?: Subscription;

  constructor(
    public dialogRef: MatDialogRef<AssociateCompanyRoleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AssociateCompanyRoleDialogData,
    private formBuilder: UntypedFormBuilder,
    private teamCompaniesService: TeamCompaniesService,
    private teamCompanyUsersService: TeamCompanyUsersService,
  ) {}

  ngOnInit(): void {
    this.companySearchSubscription = this.companySearchControl.valueChanges.subscribe(() => this.applyCompanyFilter());
    this.formSubscription = this.form.valueChanges.subscribe(() => {
      this.errorMessage = '';
      this.successMessage = '';
    });
    this.loadDialogData();
  }

  ngOnDestroy(): void {
    this.companySearchSubscription?.unsubscribe();
    this.formSubscription?.unsubscribe();
  }

  async loadDialogData(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const [companies, associations] = await Promise.all([
        this.teamCompaniesService.getCompanies(),
        this.teamCompanyUsersService.getCompanyRoleAssociationsForUser(this.data.user.id),
      ]);

      this.companies = (companies || [])
        .filter((company: any) => company?.type === 'employer')
        .sort((left: any, right: any) => `${left.name || ''}`.localeCompare(`${right.name || ''}`));
      this.associations = associations || [];
      this.applyCompanyFilter();
    } catch (error) {
      this.errorMessage = 'Unable to load companies for this user.';
    } finally {
      this.loading = false;
    }
  }

  get selectedAssociationExists(): boolean {
    const companyId = this.form.get('companyId')?.value;
    const role = this.form.get('role')?.value;
    return this.associations.some((association) =>
      association.companyId === companyId && association.role === role
    );
  }

  get canSave(): boolean {
    return this.form.valid && !this.loading && !this.saving && !this.removingAssociationId && !this.selectedAssociationExists;
  }

  async save(): Promise<void> {
    if (this.selectedAssociationExists) {
      this.errorMessage = 'This user already has that role for the selected company.';
      return;
    }

    if (!this.form.valid) {
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      await this.teamCompanyUsersService.createCompanyUserAsSuperadminRoleAssociation(
        this.form.get('companyId')?.value,
        this.data.user.id,
        this.form.get('role')?.value,
      );
      this.associations = await this.teamCompanyUsersService.getCompanyRoleAssociationsForUser(this.data.user.id);
      this.successMessage = 'Company role associated.';
    } catch (error: any) {
      if (error?.status === 409) {
        this.errorMessage = 'This user already has that role for the selected company.';
      } else {
        this.errorMessage = 'Unable to associate this company role.';
      }
    } finally {
      this.saving = false;
    }
  }

  async removeAssociation(association: CompanyRoleAssociation): Promise<void> {
    if (!association?.id || this.saving || this.removingAssociationId) {
      return;
    }

    this.removingAssociationId = association.id;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      await this.teamCompanyUsersService.deleteCompanyUserAsSuperadminRoleAssociation(association.id);
      this.associations = await this.teamCompanyUsersService.getCompanyRoleAssociationsForUser(this.data.user.id);
      this.successMessage = 'Company role removed.';
    } catch (error) {
      this.errorMessage = 'Unable to remove this company role.';
    } finally {
      this.removingAssociationId = '';
    }
  }

  close(): void {
    this.dialogRef.close(this.associations);
  }

  trackByCompanyId(_index: number, company: any): string {
    return company.id;
  }

  trackByAssociationId(_index: number, association: CompanyRoleAssociation): string {
    return association.id;
  }

  private applyCompanyFilter(): void {
    const search = `${this.companySearchControl.value || ''}`.trim().toLowerCase();

    this.filteredCompanies = this.companies.filter((company: any) => {
      const label = `${company.name || ''} ${company.domainName || ''}`.toLowerCase();
      return !search || label.includes(search);
    });
  }
}
